import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { Mail, MessageSquare, MessagesSquare, Apple, Send, RefreshCw } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";

const EVENT_LABELS = {
  registration: "Application submitted",
  approved: "Account approved",
  rejected: "Account rejected",
  wallet_topup: "Wallet top-up succeeded",
  shipment_booked: "Shipment booked",
  delivery_status: "Delivery status update",
  selle_check_payout: "Selle Check claim payout",
};

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "sms", label: "SMS", icon: MessageSquare },
  { key: "whatsapp", label: "WhatsApp", icon: MessagesSquare },
];

export default function Notifications() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [prefs, setPrefs] = useState({});
  const [testChannel, setTestChannel] = useState("email");
  const [testTo, setTestTo] = useState("");
  const [testMessage, setTestMessage] = useState("SellePack test message");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/notifications/status"),
        api.get("/notifications/preferences"),
      ]);
      setStatus(s.data);
      setPhone(p.data.phone || "");
      setWhatsapp(p.data.whatsapp || "");
      setPrefs(p.data.prefs || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load preferences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePref = (event, channel) => {
    setPrefs((prev) => ({
      ...prev,
      [event]: { ...prev[event], [channel]: !prev[event]?.[channel] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/notifications/preferences", { phone, whatsapp, prefs });
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient"); return; }
    setTesting(true);
    try {
      const r = await api.post("/notifications/test", {
        channel: testChannel, to: testTo.trim(), message: testMessage,
      });
      if (r.data.ok) {
        toast.success(r.data.simulated ? `Sent (simulated) — keys not configured` : `Sent via ${testChannel}`);
      } else {
        toast.error(`${testChannel} failed: ${r.data.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-sm text-zinc-500">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Notifications</div>
          <h1 data-testid="notifications-title" className="font-display text-3xl font-black tracking-tighter">Email, SMS & WhatsApp</h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Choose how SellePack reaches you. SMS to iPhone users automatically renders in iMessage (green bubble);
            native blue-bubble iMessage requires Apple Business Register approval and is not yet available.
          </p>
        </div>
        <button data-testid="notifications-reload" onClick={load} className="text-sm text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1">
          <RefreshCw size={14} /> Reload
        </button>
      </div>

      {/* Channel status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { k: "email", icon: Mail, label: "Email", vendor: "SendGrid" },
          { k: "sms", icon: MessageSquare, label: "SMS", vendor: "Twilio" },
          { k: "whatsapp", icon: MessagesSquare, label: "WhatsApp", vendor: "Twilio" },
          { k: "imessage", icon: Apple, label: "iMessage", vendor: "Apple" },
        ].map(({ k, icon: Icon, label, vendor }) => {
          const s = status?.[k] || {};
          const ok = s.configured;
          return (
            <div key={k} data-testid={`channel-status-${k}`} className={`border rounded-md p-4 ${ok ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-white"}`}>
              <div className="flex items-center gap-2"><Icon size={16} className={ok ? "text-emerald-700" : "text-zinc-500"} />
                <span className="font-display font-bold text-sm">{label}</span>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-2">{vendor}</div>
              <div className={`text-xs mt-1 ${ok ? "text-emerald-700" : "text-zinc-500"}`}>
                {ok ? `Live${s.mode === "sandbox" ? " (sandbox)" : ""}` : k === "imessage" ? "Not available" : "Not configured"}
              </div>
              {s.from && <div className="text-[11px] text-zinc-500 mt-1 truncate" title={s.from}>{s.from}</div>}
            </div>
          );
        })}
      </div>

      {/* Contact details */}
      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6">
        <div className="font-display text-lg font-bold mb-1">Where to reach you</div>
        <div className="text-sm text-zinc-600 mb-4">Phone numbers must be in E.164 format (e.g. <code>+15551234567</code>).</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Phone (SMS)</label>
            <input data-testid="notifications-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+15551234567"
              className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">WhatsApp</label>
            <input data-testid="notifications-whatsapp-input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+15551234567 (defaults to phone)"
              className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex" />
          </div>
        </div>
      </div>

      {/* Preferences matrix */}
      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6 overflow-x-auto">
        <div className="font-display text-lg font-bold mb-1">Per-event preferences</div>
        <div className="text-sm text-zinc-600 mb-4">Toggle which channels deliver each event.</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="text-left py-2 font-medium">Event</th>
              {CHANNELS.map(({ key, label }) => (
                <th key={key} className="py-2 font-medium w-24 text-center">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(prefs).map((evt) => (
              <tr key={evt} className="border-b border-zinc-100">
                <td className="py-3 pr-4">{EVENT_LABELS[evt] || evt}</td>
                {CHANNELS.map(({ key }) => (
                  <td key={key} className="text-center">
                    <input
                      data-testid={`pref-${evt}-${key}`}
                      type="checkbox"
                      checked={!!prefs[evt]?.[key]}
                      onChange={() => togglePref(evt, key)}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-5 flex justify-end">
          <button data-testid="notifications-save-btn" onClick={save} disabled={saving}
            className="brand-bg text-white px-5 py-2 rounded-sm text-sm font-medium disabled:opacity-50">
            {saving ? "Saving…" : "Save preferences"}
          </button>
        </div>
      </div>

      {/* Test sender */}
      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-6">
        <div className="font-display text-lg font-bold mb-1">Send a test message</div>
        <div className="text-sm text-zinc-600 mb-4">Verify your channel works end-to-end before relying on it.</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <select data-testid="test-channel-select" value={testChannel} onChange={(e) => setTestChannel(e.target.value)}
            className="border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white">
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <input data-testid="test-to-input" value={testTo} onChange={(e) => setTestTo(e.target.value)}
            placeholder={testChannel === "email" ? "you@example.com" : "+15551234567"}
            className="border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex sm:col-span-2" />
        </div>
        <textarea data-testid="test-message-input" value={testMessage} onChange={(e) => setTestMessage(e.target.value)}
          rows={2} className="mt-3 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" />
        <div className="mt-3 flex justify-end">
          <button data-testid="test-send-btn" onClick={sendTest} disabled={testing}
            className="bg-zinc-900 text-white px-5 py-2 rounded-sm text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2">
            <Send size={14} /> {testing ? "Sending…" : "Send test"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
